import { NOT_A_NUMBER } from '@/const/regex'

/**
 * 배열내의 오브젝트 KeyName 을 변경합니다.
 *
 * @param  arr 키 이름을 바꿀 대상 오브젝트를 포함하는 배열
 * @param  newKeys 새로운 키
 *         ex) { text: 'title' } 텍스트 키가 타이틀 키로 변경됩니다.
 *
 * @return 키가 변경된 오브젝트 배열
 */
export function replaceObjectKeyName<T>(
  arr: T[],
  newKeys: { [key: string]: any }
): { [key: string]: any }[] {
  return arr.map((obj): { [key: string]: any } => {
    return replaceKeyName(obj, newKeys);
  });
}

/**
 * 오브젝트의 KeyName 을 변경합니다.
 * Nested Object 일 경우 replaceObjectKeyName() 을 재귀의 형태로 사용합니다.
 *
 * @param  obj 키 이름을 바꿀 대상 오브젝트
 * @param  newKeys 새로운 키
 *         ex) { text: 'title' } 텍스트 키가 타이틀 키로 변경됩니다.
 *
 * @return 키가 변경된 오브젝트
 */
export function replaceKeyName(
  obj: { [key: string]: any },
  newKeys: { [key: string]: any }
): { [key: string]: any } {
  const keyValues = Object.keys(obj).map((key): { [key: string]: any } => {
    const newKey = newKeys[key] || key;

    if (Array.isArray(obj[key])) {
      const temp = replaceObjectKeyName(obj[key], newKeys)
      return {
        [newKey]: temp
      }
    }

    return {
      [newKey]: obj[key],
    };
  });

  return Object.assign({}, ...keyValues);
}

/**
 * 배열의 인덱스를 1:1 교체합니다.
 *
 * @arr        대상 배열
 * @indexAddr1 교체할 인덱스
 * @indexAddr2 교체할 인덱스
 *
 * @return     교체된 배열
 */
export function arrayElementSwap<T>(arr: T[], indexAddr1: number, indexAddr2: number): T[] {
  const tempArr: T[] = arr.concat();
  const temp: T = tempArr[indexAddr1];
  tempArr[indexAddr1] = tempArr[indexAddr2];
  tempArr[indexAddr2] = temp;

  return tempArr;
}

export const clone = data => JSON.parse(JSON.stringify(data));

export const range = length => Array.from({length}, (_, i) => i)

/**
 * NONE: 회원 + 비회원 조회 가능
 * ALL: 회원만 조회 가능
 * ANY: 일부 회원만 조회 가능, info 의 번호로 관리
 */

export type NoneAllAny = 'ALL'|'NONE'|'ANY';

export interface NoneAllAnyOption {
  check: NoneAllAny,
  info: number[]
}

export interface EncodeNoneAllAnyOption {
  check: NoneAllAny,
  info: 'ALL'|string
}

export function encodeRadioAllOrNoneOrAny(radioOption: NoneAllAnyOption, id: string): EncodeNoneAllAnyOption {
  switch (radioOption.check) {
    case 'NONE':
      return { check: 'NONE', info: 'ALL' }
    case 'ALL':
      return { check: 'ANY', info: 'ALL'}
    case 'ANY':
      return { check: 'ANY', info: radioOption.info.map(v => `${id}-${v}`).join(',') }
  }
}

export function decodeRadioAllOrNoneOrAny(radioOption: EncodeNoneAllAnyOption): NoneAllAnyOption {
  switch (radioOption.check) {
    case 'NONE':
      return { check: 'NONE', info: [] }
    case 'ANY':
      if (radioOption.info === 'ALL') {
        return { check: 'ALL', info: [] }
      }
        return { check: 'ANY', info: Array.isArray(radioOption.info) ?
          radioOption.info.map(v => Number(v.split('-')[1])).filter(v => v) :
          radioOption.info.split(',').map(v => Number(v.split('-')[1])).filter(v => v)
      }
  }
}

export function decodeCheckYorN(checkOption): {[propName: string]: 'Y'|'N'} {
  // arg : {[propName: string]: boolean}
  const newObj: any = {...checkOption}
  Object.keys(checkOption).forEach(key => {
    return newObj[key] = newObj[key] === true ? 'Y':'N'
  })
  return newObj
}

export function indexInParent(node: HTMLElement): number {
  const children = node.parentNode.childNodes;
  let index = 0;
  for (let i = 0; i < children.length; i++) {
    if (children[i] === node) return index;
    if (children[i].nodeType === 1) index++;
  }
  return -1;
}

export function onlyNumber(replaceValue: string): string {
  return replaceValue.replace(NOT_A_NUMBER, '');
}

export function commaNumStringToNumber(replaceValue: string): number {
  return Number(replaceValue.replace(NOT_A_NUMBER, ''));
}